/**
 * EstimateOrderView (TASK-271, AC-7)
 *
 * Renders an e-commerce *order* view for an estimate whose
 * `mode === 'ecommerce'`. Instead of the construction-estimate sections
 * (work groups, data sources, overhead), it shows an order table —
 * Товар / Кол-во / Цена / Сумма — followed by Подытог / Доставка / Наценка /
 * Итого rows.
 *
 * Defensive about missing data (old orders / partial payloads):
 * - subtotal falls back to Σ(item.total ?? price × quantity);
 * - delivery / markup rows render only when present;
 * - the grand total falls back to subtotal + delivery + markup when
 *   `total_cost` is absent.
 *
 * KAN-746: the totals block is shared with the order card (OrderTotals).
 * KAN-755: "Скидка" and "Промокод" rows when the order has them.
 */
import type { ReactNode } from 'react';
import { ShoppingCart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { OrderTotals } from '@/components/orders/OrderTotals';
import { discountAmount, toAmount as toNumber } from '@/components/orders/orderFormat';
import type { EstimateDetail, LineItem } from '@/types/estimate';

interface EstimateOrderViewProps {
  estimate: EstimateDetail;
  /** KAN-744: manager control of the order (EstimateManagerSelect), shown in the header. */
  managerControl?: ReactNode;
}

/** Format a number with the Russian thousands separator and 2 decimals. */
function formatMoney(value: number): string {
  return value.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Per-item unit price for an order position (e-commerce `price`). */
function itemPrice(item: LineItem): number {
  return toNumber(item.price);
}

/** Per-item line total: explicit `total`, else price × quantity. */
function itemTotal(item: LineItem): number {
  if (item.total !== undefined && item.total !== null) {
    return toNumber(item.total);
  }
  return itemPrice(item) * toNumber(item.quantity);
}

export function EstimateOrderView({ estimate, managerControl }: EstimateOrderViewProps) {
  const items = estimate.line_items || [];

  // Subtotal: prefer the server value, else sum the item line totals.
  const computedSubtotal = items.reduce((sum, item) => sum + itemTotal(item), 0);
  const subtotal =
    estimate.subtotal !== undefined && estimate.subtotal !== null
      ? toNumber(estimate.subtotal)
      : computedSubtotal;

  const hasDelivery = estimate.delivery !== undefined && estimate.delivery !== null;
  const hasMarkup = estimate.markup !== undefined && estimate.markup !== null;
  const delivery = toNumber(estimate.delivery);
  const markup = toNumber(estimate.markup);
  const discount = discountAmount(estimate.discount);

  // Grand total: prefer total_cost, else subtotal - discount + delivery + markup.
  const totalCost = toNumber(estimate.total_cost);
  const total = totalCost > 0 ? totalCost : subtotal - (discount ?? 0) + delivery + markup;

  return (
    <Card data-testid="order-view" className="mt-6">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-lg flex items-center gap-2">
          <ShoppingCart className="h-5 w-5" />
          Заказ
        </CardTitle>
        {managerControl}
      </CardHeader>
      <CardContent className="p-0">
        {items.length > 0 ? (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <Table data-testid="order-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Товар</TableHead>
                    <TableHead className="text-right">Кол-во</TableHead>
                    <TableHead className="text-right">Цена</TableHead>
                    <TableHead className="text-right">Сумма</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={index} data-testid={`order-item-${index}`}>
                      <TableCell className="text-gray-500">{index + 1}</TableCell>
                      <TableCell
                        data-testid={`order-item-name-${index}`}
                        className="font-medium">
                        {item.name}
                      </TableCell>
                      <TableCell
                        data-testid={`order-item-qty-${index}`}
                        className="text-right">
                        {toNumber(item.quantity)}
                      </TableCell>
                      <TableCell
                        data-testid={`order-item-price-${index}`}
                        className="text-right">
                        {formatMoney(itemPrice(item))}
                      </TableCell>
                      <TableCell
                        data-testid={`order-item-total-${index}`}
                        className="text-right font-medium">
                        {formatMoney(itemTotal(item))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile card view */}
            <div className="space-y-3 p-4 md:hidden">
              {items.map((item, index) => (
                <div
                  key={index}
                  data-testid={`order-item-mobile-${index}`}
                  className="border rounded-lg p-3">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="text-xs text-gray-400">#{index + 1}</span>
                    <span className="text-sm font-semibold text-gray-900 text-right">
                      {formatMoney(itemTotal(item))} ₽
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1">{item.name}</p>
                  <p className="text-xs text-gray-500">
                    {toNumber(item.quantity)} &times; {formatMoney(itemPrice(item))} ₽
                  </p>
                </div>
              ))}
            </div>

            <OrderTotals
              subtotal={subtotal}
              delivery={hasDelivery ? delivery : null}
              markup={hasMarkup ? markup : null}
              total={total}
              discount={discount}
              promoCode={estimate.promo_code || null}
            />
          </>
        ) : (
          <div
            data-testid="order-empty"
            className="flex flex-col items-center justify-center py-12">
            <ShoppingCart className="h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500">В заказе пока нет товаров.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default EstimateOrderView;
